import JSX, {Component, h} from "preact";
import {Configuration, Field, Provider} from "./types";
import {isFunction} from "../../libs/preact-entry";
import Header from "../header";
import Panel from "../panel";

interface Props {
  provider: Provider
  onMount: () => void
  onSubmit: (configuration: Configuration) => void
  children: JSX.Element,
}

interface State {
  configuration: Configuration
  errors: {
    [item: string]: any
  }
}

export type ProvidedItems = {
  configuration: Configuration
  onSubmit: (configuration: Configuration) => void
  getError: (fieldName: string) => string | null
  getSubmitButtonProps: () => {}
  onInputChange: (name: string, value: string) => void
}

export default class AbstractProvider extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      configuration: this.getEmptyConfiguration(),
      errors: {}
    }
  }

  getEmptyConfiguration() {
    const {fields} = this.props.provider
    const emptyConfiguration: Configuration = {}

    fields.forEach((field: Field) => {
      emptyConfiguration[field.name] = ''
    })

    return emptyConfiguration
  }

  componentWillReceiveProps(nextProps: Readonly<Props>, nextContext: any): void {
    const {isInstalled} = this.props.provider
    if (isInstalled) {
      const {configuration, errors} = nextProps.provider
      this.setState({configuration, errors})
      return
    }

    const {errors} = nextProps.provider
    this.setState({errors})
  }

  componentDidMount(): void {
    if (this.props.provider.isInstalled) this.props.onMount()
  }

  async onSubmit(e: Event): Promise<void> {
    e.preventDefault()
    if (this.validateForm()) {
      const {configuration} = this.state
      this.props.onSubmit(configuration)
    }
  }

  onInputChange(name: string, value: string): void {
    const {configuration, errors} = this.state
    const inputIsEmpty = !value.trim()

    const newConfiguration = {...configuration, ...{[name]: value}}

    if (inputIsEmpty) {
      this.setState({
        configuration: newConfiguration,
        errors: Object.assign(errors, {[name]: 'This is a required field'})
      })
      return
    }

    delete errors![name]

    this.setState({
      configuration: newConfiguration,
      errors
    })
  }

  validateForm(): boolean {
    let errors = {}
    const {configuration} = this.state

    Object.keys(configuration).forEach(item => {
      const formField = configuration[item]
      const value = formField.trim()
      if (!value) Object.assign(errors, {[item]: 'This is a required field'})
    })

    if (Object.keys(errors).length > 0) {
      this.setState({errors})
      return false
    }

    return true
  }

  getSubmitButtonProps(): {} {
    const {isInstalled} = this.props.provider
    return {
      type: 'submit',
      title: isInstalled ? 'Save changes' : 'Install',
      variant: isInstalled ? 'success' : 'primary'
    }
  }

  getError(fieldName: string): string | null {
    const {errors} = this.state
    if (Object.keys(errors).length === 0) return null
    const errorField = Object.keys(errors).filter((error: string) => error === fieldName)[0]
    return errors[errorField]
  }

  isReadyToRender(): boolean {
    const {isInstalled} = this.props.provider

    if (!isInstalled) return true

    const {configuration} = this.state
    const isConfigurationEmpty = Object.keys(configuration).filter(item => configuration[item]).length > 0

    return isInstalled && isConfigurationEmpty
  }

  render({children, provider: {name, label, isInstalled}}: Props, { configuration }: State) {
    if (!this.isReadyToRender()) return null
    if (isFunction(children[0])) {
      return <div class={`${name}-setup`}>
        <Header
            title={label}
            disableCancelButton
            disableSaveButton
            hasBreadcrumb
            breadcrumbText='Back to SSO overview'
            breadcrumbLink='/integration/sso'
            statusLabel={isInstalled && 'Installed' || undefined}
        />
        <Panel title={`${label} setup`} class='sso-settings'>
          { children[0]({
            configuration: configuration,
            onSubmit: this.onSubmit.bind(this),
            getError: this.getError.bind(this),
            getSubmitButtonProps: this.getSubmitButtonProps.bind(this),
            onInputChange: this.onInputChange.bind(this),
          }) }
        </Panel>
      </div>
    }
    return null
  }
}
